/**
 * Wording and expiry for a conversation mute. Kept out of the components so the same rule — "a mute
 * whose end has passed is not a mute" — drives the list icon, the thread header and the menu, and
 * so it can be tested without rendering anything.
 */

/** The API stores "until I turn it back on" as a year-9999 instant; anything from here on reads as forever. */
const FOREVER_FROM_YEAR = 9000;

export function isMuted(mutedUntil: string | null | undefined, now: Date = new Date()): boolean {
  if (!mutedUntil) return false;
  const ends = new Date(mutedUntil).getTime();
  return Number.isFinite(ends) && ends > now.getTime();
}

/** Total unread across conversations, leaving out muted ones — muting a noisy channel must also
 * silence its contribution to the "N new" / nav badge, not only its notifications. The per-row
 * count still shows (greyed) so the person can see there is something there.
 *
 * Lives here, not in ConversationList.tsx, because the top bar's chat badge needs it on every page:
 * importing it from the list component pulled the whole chat UI and its markdown renderer into the
 * app's first load. */
export function unreadTotal(
  conversations: ReadonlyArray<{ unreadCount: number; mutedUntil?: string | null }>,
  now: Date = new Date(),
): number {
  return conversations.reduce(
    (sum, c) => (isMuted(c.mutedUntil, now) ? sum : sum + c.unreadCount),
    0,
  );
}

function sameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  );
}

/** "Muted until 3:45 PM", "Muted until Sep 26, 3:45 PM", "Muted until you turn it back on" — or null when not muted. */
export function muteLabel(mutedUntil: string | null | undefined, now: Date = new Date()): string | null {
  if (!isMuted(mutedUntil, now)) return null;
  const ends = new Date(mutedUntil as string);
  if (ends.getUTCFullYear() >= FOREVER_FROM_YEAR) return 'Muted until you turn it back on';

  const time = ends.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (sameLocalDay(ends, now)) return `Muted until ${time}`;
  const day = ends.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return `Muted until ${day}, ${time}`;
}

export const MUTE_CHOICES = [
  { duration: '1h', label: 'Mute for 1 hour' },
  { duration: '8h', label: 'Mute for 8 hours' },
  { duration: '24h', label: 'Mute for 24 hours' },
  { duration: '7d', label: 'Mute for 7 days' },
  { duration: 'forever', label: 'Mute until I turn it back on' },
] as const;
